import { type FeatureEntity, PlanStatus } from "@src/gql/graphql";
import { useMemo } from "react";
import { useMyPlanSuspense } from "../queries/fetch-plan";
import { useMyPlanEntityMeteredList } from "../queries/fetch-plan-entity-metered";
import {
	type FeatureGateInput,
	type FeatureGateProps,
	type FeatureGateResult,
	type FeatureStats,
	isFeatureEnabled,
	useNormalizedFeatures,
} from "./base";
import { FeatureGateRender } from "./render-feature-gate";

export type EntityFeatureGateProps = {
	feature: FeatureGateInput;
	entity: {
		id: string;
		type: FeatureEntity;
	};
} & FeatureGateProps;

export const EntityFeatureGate = (props: EntityFeatureGateProps) => {
	const { feature, entity } = props;

	const configs = useNormalizedFeatures(feature, "entity", entity);

	if (configs.length === 0) {
		return <FeatureGateRender {...props} enabled={true} />;
	}

	const sessionConfigs = configs.filter((config) => config.scope === "session");
	const entityConfigs = configs.filter((config) => config.scope === "entity");

	const { data: plan } = useMyPlanSuspense();
	const planFeatures = plan?.features ?? [];

	const meteredListConfigs = useMemo(
		() =>
			entityConfigs.map((config) => ({
				entityId: config.entity?.id ?? entity.id,
				entityType: config.entity?.type ?? entity.type,
				supportedFeature: config.feature,
			})),
		[entityConfigs, entity],
	);
	const entityQueryResults = useMyPlanEntityMeteredList(meteredListConfigs);

	let enabled = true;
	let stats: FeatureStats | undefined;
	const details: FeatureGateResult[] = [];

	if (sessionConfigs.length > 0) {
		const planActive =
			plan?.status === PlanStatus.Active ||
			plan?.status === PlanStatus.InTrial ||
			plan?.status === PlanStatus.Cancelled;

		enabled = enabled && !!planActive;

		for (const config of sessionConfigs) {
			const featureState = planFeatures.find(
				(f) => f.feature === config.feature,
			);

			const state = isFeatureEnabled(featureState);
			enabled = enabled && state.enabled;

			if (state.stats) {
				stats = state.stats;
			}

			details.push({ ...config, enabled: state.enabled, stats: state.stats });
		}
	}

	if (entityConfigs.length > 0) {
		for (let index = 0; index < entityConfigs.length; index++) {
			const result = entityQueryResults[index];
			const state = isFeatureEnabled(result?.data);
			enabled = enabled && state.enabled;

			if (state.stats) {
				stats = state.stats;
			}

			details.push({
				...entityConfigs[index],
				enabled: state.enabled,
				stats: state.stats,
			});
		}
	}

	if (stats) {
		return (
			<FeatureGateRender
				{...props}
				enabled={enabled}
				stats={stats}
				details={details}
			/>
		);
	}

	return <FeatureGateRender {...props} enabled={enabled} details={details} />;
};
